import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getCookiePath, getPrivacyPath } from '../utils/routes'
import { setConsent } from '../lib/analytics'

// Consent is stored in a real cookie (visible under Application → Cookies in
// devtools) so it is sent with requests and survives like any other cookie.
const CONSENT_COOKIE = 'mha_cookie_consent'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 180 // 180 days (GDPR: re-ask within ~6 months)

interface Prefs {
  analytics: boolean
  marketing: boolean
}

function getLocaleFromPath(pathname: string): 'hu' | 'ro' {
  return pathname.startsWith('/hu') ? 'hu' : 'ro'
}

const texts = {
  hu: {
    title: 'Sütik kezelése',
    text: 'Sütiket használunk a weboldal működéséhez, a forgalom elemzéséhez és — az Ön hozzájárulásával — marketing célokra. Ön döntheti el, mit engedélyez.',
    acceptAll: 'Összes elfogadása',
    rejectAll: 'Összes elutasítása',
    customize: 'Beállítások',
    save: 'Beállítások mentése',
    privacy: 'Adatvédelmi irányelvek',
    cookie: 'Cookie-szabályzat',
    necessary: {
      title: 'Szükséges',
      desc: 'A weboldal alapvető működéséhez szükséges. Mindig aktív.',
      always: 'Mindig aktív',
    },
    analytics: {
      title: 'Analitika',
      desc: 'Segít megérteni, hogyan használják a látogatók az oldalt (pl. Google Analytics).',
    },
    marketing: {
      title: 'Marketing',
      desc: 'Hirdetések és pixelek a releváns tartalom megjelenítéséhez.',
    },
  },
  ro: {
    title: 'Gestionarea cookie-urilor',
    text: 'Folosim cookie-uri pentru funcționarea site-ului, analiza traficului și — cu acordul dvs. — în scopuri de marketing. Puteți alege ce permiteți.',
    acceptAll: 'Accept toate',
    rejectAll: 'Refuz toate',
    customize: 'Setări',
    save: 'Salvează preferințele',
    privacy: 'Politica de confidențialitate',
    cookie: 'Politica de cookie-uri',
    necessary: {
      title: 'Necesare',
      desc: 'Necesare pentru funcționarea de bază a site-ului. Întotdeauna active.',
      always: 'Întotdeauna active',
    },
    analytics: {
      title: 'Analitică',
      desc: 'Ne ajută să înțelegem cum este folosit site-ul (ex. Google Analytics).',
    },
    marketing: {
      title: 'Marketing',
      desc: 'Reclame și pixeli pentru a afișa conținut relevant.',
    },
  },
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const [customize, setCustomize] = useState(false)
  const [prefs, setPrefs] = useState<Prefs>({ analytics: false, marketing: false })
  const { pathname } = useLocation()
  const locale = getLocaleFromPath(pathname)
  const t = texts[locale]

  useEffect(() => {
    const stored = readConsent()
    if (!stored) {
      setVisible(true)
    } else {
      // Re-apply the saved choice on every load so GTM-managed tags know the
      // consent state again (Consent Mode defaults to denied in index.html).
      applyConsent(stored)
      setPrefs(stored)
    }
  }, [])

  function resolve(p: Prefs) {
    writeConsent(p)
    applyConsent(p)
    setVisible(false)
    setCustomize(false)
  }

  const acceptAll = () => resolve({ analytics: true, marketing: true })
  const rejectAll = () => resolve({ analytics: false, marketing: false })
  const saveCustom = () => resolve(prefs)

  if (!visible) return null

  return (
    // Full-screen overlay blocks interaction with the site until the user
    // resolves consent. No close-on-backdrop — a choice must be made.
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="w-full max-w-lg rounded-card bg-white p-6 shadow-hover md:p-8">
        <h2
          id="cookie-consent-title"
          className="font-heading text-xl font-semibold text-secondary"
        >
          {t.title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-text-secondary">{t.text}</p>

        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <Link
            to={getPrivacyPath(locale)}
            className="text-primary underline underline-offset-2 hover:text-primary-hover"
          >
            {t.privacy}
          </Link>
          <Link
            to={getCookiePath(locale)}
            className="text-primary underline underline-offset-2 hover:text-primary-hover"
          >
            {t.cookie}
          </Link>
        </p>

        {customize && (
          <div className="mt-5 space-y-4 border-t border-border pt-5">
            <Category
              title={t.necessary.title}
              desc={t.necessary.desc}
              checked
              disabled
              alwaysLabel={t.necessary.always}
            />
            <Category
              title={t.analytics.title}
              desc={t.analytics.desc}
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <Category
              title={t.marketing.title}
              desc={t.marketing.desc}
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            />
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          {customize ? (
            <>
              <button onClick={rejectAll} className={outlineBtn}>
                {t.rejectAll}
              </button>
              <button onClick={saveCustom} className={primaryBtn}>
                {t.save}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setCustomize(true)} className={outlineBtn}>
                {t.customize}
              </button>
              <button onClick={rejectAll} className={outlineBtn}>
                {t.rejectAll}
              </button>
              <button onClick={acceptAll} className={primaryBtn}>
                {t.acceptAll}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const primaryBtn =
  'px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-btn hover:bg-primary-hover transition-colors'
// Black outline only, transparent fill.
const outlineBtn =
  'px-5 py-2.5 text-sm font-medium text-secondary bg-transparent border border-secondary rounded-btn hover:bg-surface transition-colors'

interface CategoryProps {
  title: string
  desc: string
  checked: boolean
  disabled?: boolean
  alwaysLabel?: string
  onChange?: (value: boolean) => void
}

function Category({ title, desc, checked, disabled, alwaysLabel, onChange }: CategoryProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-sm font-medium text-secondary">{title}</h3>
        <p className="mt-0.5 text-xs leading-relaxed text-text-muted">{desc}</p>
      </div>
      {disabled ? (
        <span className="flex-shrink-0 text-xs font-medium text-text-muted">{alwaysLabel}</span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={title}
          onClick={() => onChange?.(!checked)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
            checked ? 'bg-primary' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              checked ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      )}
    </div>
  )
}

// ── Consent persistence + application ──

function applyConsent(p: Prefs) {
  setConsent({
    analytics: p.analytics ? 'granted' : 'denied',
    ad: p.marketing ? 'granted' : 'denied',
  })
}

function writeConsent(p: Prefs) {
  const value = `a${p.analytics ? 1 : 0}_m${p.marketing ? 1 : 0}`
  document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
}

function readConsent(): Prefs | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + CONSENT_COOKIE + '=([^;]*)'))
  if (!match) return null
  const value = decodeURIComponent(match[1])
  return { analytics: value.includes('a1'), marketing: value.includes('m1') }
}
